import React, { useState } from "react";
import Modal from "react-bootstrap/Modal";
import { Button } from "react-bootstrap";
import TextBox from "@/components/Common/TextBox";
import _ from "lodash";
import useIsMobile from "@/hooks/useIsMobile";
import { capitalizeString } from "@/actions/common";
import { getGeoLocation } from "@/services/commonServices";
import { formatAddressForDisplay } from "@/helpers/common";

const LocationField = ({ value, error, onChange, onSelect, onLookupError }) => {
  const [locationList, setLocationList] = useState([]);

  const handleChange = (val) => {
    const capitalizedValue = capitalizeString(val);
    onChange(capitalizedValue);
    if (capitalizedValue.trim() !== "") {
      const reqPayload = `address=${capitalizedValue}&components=country:US`;
      getGeoLocation(reqPayload)
        .then((data) => {
          const results = _.get(data, "results", []);
          if (results.length > 0) {
            const formatted_address = _.get(results[0], "formatted_address", "");
            if (formatted_address !== "United States") {
              setLocationList(results);
              return;
            }
          }
          setLocationList([]);
        })
        .catch(() => {
          setLocationList([]);
          onLookupError?.();
        });
    } else {
      setLocationList([]);
    }
  };

  const handleSelect = (address) => {
    const firstAddressComponent = _.get(address, "address_components", []);
    const formatted_address = _.get(address, "formatted_address", "");
    const stateSearched = firstAddressComponent.filter(
      (ca) => ca.types?.[0] === "administrative_area_level_1"
    );
    const locality = firstAddressComponent.filter(
      (ca) => ca.types?.[0] === "locality"
    );
    const state = stateSearched.length ? _.get(stateSearched[0], "short_name", "") : "";
    const city = locality.length ? _.get(locality[0], "long_name", "") : "";
    const selectedValue = city ? city + ", " + state : formatted_address;
    onSelect(selectedValue);
    setLocationList([]);
  };

  return (
    <div className="textbox-wrap mb-3 position-relative">
      <label className="form-label">
        Location <span className="text-danger">*</span>
      </label>
      <TextBox
        name="location"
        type="text"
        tabIndex="1"
        maxLength={40}
        errorMessage={error}
        value={value}
        placeholder="City, State"
        className={error ? "form-control validation-txtfld" : "form-control"}
        onChange={(e) => handleChange(e.target.value)}
      />
      {locationList.length > 0 && (
        <div className="location-result-wrap mw-100 mt-4">
          <ul>
            {locationList.map((address, i) => (
              <li key={i}>
                <a
                  role="button"
                  href={void 0}
                  onClick={() => handleSelect(address)}
                >
                  {formatAddressForDisplay(_.get(address, "formatted_address", ""))}
                </a>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

export const CreateProfileLocationModal = ({ show, onContinue }) => {
  const isMobile = useIsMobile();
  const [location, setLocation] = useState("");
  const [locationError, setLocationError] = useState("");
  const [isLocationSelected, setIsLocationSelected] = useState(false);

  // Continue stays enabled and validates on click. Disabling it left users
  // with a dead button and no way to tell what was wrong, and this modal has
  // no exit - it re-arms itself on refresh.
  const handleContinue = () => {
    if (!location.trim()) {
      setLocationError("Location is required");
      return;
    }
    if (!isLocationSelected) {
      setLocationError("Select a location from the list of suggestions");
      return;
    }
    setLocationError("");
    onContinue(location);
  };

  return (
    <Modal
      show={show}
      size="md"
      aria-labelledby="contained-modal-title-vcenter"
      centered
      fullscreen={isMobile}
      backdrop="static"
      keyboard={false}
      className={isMobile ? "" : "modal-width-548"}
    >
      <Modal.Body
        className="signup-modal-main"
        style={
          isMobile
            ? { justifyContent: "center", display: "flex", flexDirection: "column" }
            : {}
        }
      >

        <h3 className="text-start">Let’s create your profile!</h3>
        <p className="mb-30 ln-24 f-size-16 para-color-first">
          We're here to help you meet you and your real estate goals. Let's get
          started by setting up your profile.{" "}
        </p>

        <LocationField
          value={location}
          error={locationError}
          onChange={(val) => {
            setLocation(val);
            // Editing after picking invalidates the selection: the downstream
            // market lookup matches on the normalized "City, ST" value.
            setIsLocationSelected(false);
            setLocationError("");
          }}
          onSelect={(val) => {
            setLocation(val);
            setIsLocationSelected(true);
            setLocationError("");
          }}
          onLookupError={() =>
            setLocationError(
              "We couldn't load locations just now. Check your connection and try again."
            )
          }
        />

        <Button
          variant="primary"
          tabIndex="2"
          className="w-100"
          onClick={handleContinue}
        >
          Continue
        </Button>
      </Modal.Body>
    </Modal>
  );
};

export default CreateProfileLocationModal;
